import type { AdventureDetail } from '../../services/GameApiService';

interface AdventureCastListProps {
  adventure: AdventureDetail;
}

/**
 * Mostra a personagem inicial e o elenco pré-gerado como cards.
 * Reutiliza os avatares neutros já gerados pela pipeline de imagens.
 */
export default function AdventureCastList({ adventure }: AdventureCastListProps) {
  const initial = adventure.initialCharacter;
  const cast = adventure.charactersPreview ?? [];

  if (!initial && cast.length === 0) return null;

  return (
    <section className="px-4 py-12 md:py-16 bg-white">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-dark mb-2 text-center">
          Com quem vais falar
        </h2>
        <p className="text-gray-600 text-center mb-8">
          Cada personagem traz uma perspectiva diferente. Vários pontos de vista, uma só história.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {initial && (
            <div className="group relative rounded-xl overflow-hidden shadow-lg aspect-square">
              {initial.avatarUrl ? (
                <img
                  src={initial.avatarThumbUrl ?? initial.avatarUrl}
                  alt={initial.name ?? 'Personagem inicial'}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-2xl font-bold">
                  {initial.name?.[0] ?? '?'}
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-dark/95 via-dark/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-bold text-sm md:text-base">
                  {initial.name}
                </p>
                <p className="text-white/70 text-xs md:text-sm">{initial.role}</p>
              </div>
              {/* "Começa aqui" badge */}
              <div className="absolute top-2 right-2 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-accent text-white">
                Começa aqui
              </div>
            </div>
          )}

          {cast.map((c) => (
            <div key={c.id} className="group relative rounded-xl overflow-hidden shadow-lg aspect-square">
              {c.avatarUrl ? (
                <img
                  src={c.avatarThumbUrl ?? c.avatarUrl}
                  alt={c.name ?? 'Personagem'}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-2xl font-bold">
                  {c.name?.[0] ?? '?'}
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-dark/95 via-dark/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-bold text-sm md:text-base">{c.name}</p>
                <p className="text-white/70 text-xs md:text-sm">{c.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}